import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column, ExpandedColumn } from '@repo/ui/data-table'
import { TableDate } from '@repo/ui/data-table-cells'
import {
  BellIcon,
  ChartIcon,
  EuroIcon,
  EyeOpenIcon,
  PlusIcon,
  UserCanceledIcon,
  UserCheckIcon,
  WithdrawIcon,
  XIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { getLatestPcrPayScale } from '../../../api/pcr-pay-scale/get-latest-pcr-pay-scale'
import { getStaffApplicationCurrentScoreCard } from '../../../api/staff-application/get-staff-application-current-score-card'
import { markStaffApplicationsAsPaid } from '../../../api/staff-application/mark-as-paid'
import { rejectStaffApplication } from '../../../api/staff-application/reject-application'
import { sendReminderToPcr } from '../../../api/staff-application/send-reminder-to-pcr'
import { validateStaffApplications } from '../../../api/staff-application/validate-application'
import { Paths } from '../../../lib/constants/path'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { languageEnumMapping } from '../../../lib/types/enums/languages/language.enum'
import { LanguageLevelEnum } from '../../../lib/types/enums/languages/language-level.enum'
import { priorityEnumMapping } from '../../../lib/types/enums/staff-application/priority.enum'
import {
  StaffApplicationStatusEnum,
  staffApplicationStatusEnumMapping
} from '../../../lib/types/enums/staff-application/staff-application-status.enum'
import { staffApplicationTypeMapping } from '../../../lib/types/enums/staff-application/staff-application-type.enum'
import { paymentStatusMapping, pcrResult } from '../../../lib/types/models/score-card/score-card'
import {
  StaffApplication,
  StaffApplicationFilters
} from '../../../lib/types/models/staff-application/staff-application'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformStaffApplicationFiltersToUrl,
  transformUrlToStaffApplicationFilters
} from '../../../lib/utils/filters-mapping/staff-applications'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import FilterDrawer from '../../elements/drawers/filter-drawer'
import { DynamicTableRef } from '../../elements/dynamic-table'
import { countFilters } from '../../elements/dynamic-table/utils/countFilters'
import NestedDataTable from '../../elements/nested-table'
import StaffApplicationMarkAsPaidForm from '../candidates/staff-application-mark-as-paid-form'
import StaffApplicationSummary from '../candidates/staff-application-summary'
import StaffApplicationsFilter from '../candidates/staff-applications-filter'
import { mapScoreCardToInput } from '../staff-application/utils'
import { formatPcrSalary, getPcrApplicationSalary } from './pcr-pay-scale.utils'
import { PcrScoreCardModal } from './pcr-score-card-form/pcr-score-card-form'

type Props = {
  pcrId: number
}

const getApplicationStatusDate = (application: StaffApplication) => {
  const pcrScoreCard = application.pcrScoreCard
  const currentScoreCard = application.currentScoreCard

  switch (application.status) {
    case StaffApplicationStatusEnum.FIRST_CONTACT_ESTABLISHED:
    case StaffApplicationStatusEnum.SECOND_CONTACT_ESTABLISHED:
    case StaffApplicationStatusEnum.THIRD_CONTACT_ESTABLISHED:
    case StaffApplicationStatusEnum.CLASSIFIED:
    case StaffApplicationStatusEnum.INTERVIEW_DONE:
      return application.statusUpdatedAt
    case StaffApplicationStatusEnum.INTERVIEW_SCHEDULED:
      return pcrScoreCard?.interviewStartDate ?? currentScoreCard?.interviewStartDate
    default:
      return undefined
  }
}

const ApplicationStatusBadge = ({ application }: { application: StaffApplication }) => {
  const status = staffApplicationStatusEnumMapping[application.status]
  const statusDate = getApplicationStatusDate(application)

  return (
    <Badge
      text={
        <span className="flex flex-col items-center leading-tight">
          <span>{status.label}</span>

          {application.dateAssigned &&
            application.status === StaffApplicationStatusEnum.TO_BE_CONTACTED && (
              <span>{DateFormatter.DDMMYYYY(application.dateAssigned)}</span>
            )}

          {statusDate && <span>{DateFormatter.DDMMYYYY(statusDate)}</span>}
        </span>
      }
      variant={status.variant}
      className={'h-auto py-1'}
    />
  )
}

const PcrApplicationsTable: React.FC<Props> = ({ pcrId }) => {
  const tableRef = React.useRef<DynamicTableRef<StaffApplication>>(null)
  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'mark-as-paid' | 'reject-application',
    StaffApplication[]
  >(['filters', 'mark-as-paid', 'reject-application'])
  const {
    modalState: recordModalState,
    selectedData: recordSelectedData,
    toggleModal: toggleRecordModal,
    closeModel: closeRecordModel
  } = useModalManager<'view-evaluation' | 'view-summary', StaffApplication>([
    'view-evaluation',
    'view-summary'
  ])
  const { navigate } = useNavigator()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [tableData, setTableData] = useState<StaffApplication[]>([])
  const [rejectApplicationWithEmailLoading, setRejectApplicationWithEmailLoading] = useState(false)
  const [rejectApplicationLoading, setRejectApplicationLoading] = useState(false)
  const languageMapping = languageEnumMapping()

  const applicationsInitialFilter: StaffApplicationFilters = useMemo(
    () => ({
      periods: [],
      diplomas: [],
      languages: [],
      statuses: [],
      types: [],
      evaluations: [],
      similarApplications: false,
      showWithdrawApplications: false,
      pcrId
    }),
    [pcrId]
  )

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<StaffApplicationFilters>({
      initialFilters: applicationsInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformStaffApplicationFiltersToUrl,
      transformUrlToFilter: transformUrlToStaffApplicationFilters
    })

  const { mutateAsync: markAsPaidMutation } = useMutation({
    mutationFn: markStaffApplicationsAsPaid,
    onError: showAxiosError
  })

  const { mutateAsync: validateStaffApplicationsMutation } = useMutation({
    mutationFn: validateStaffApplications,
    onError: showAxiosError
  })

  const { mutateAsync: rejectApplicationMutation } = useMutation({
    mutationFn: rejectStaffApplication,
    onError: showAxiosError
  })

  const { data: payScale } = useQuery({
    queryKey: ['latestPcrPayScale'],
    queryFn: getLatestPcrPayScale
  })

  const { data: currentScoreCard, isPending: isCurrentScoreCardPending } = useQuery({
    queryFn: () => {
      if (recordSelectedData) return getStaffApplicationCurrentScoreCard(recordSelectedData.id)
    },
    queryKey: ['score-card', recordSelectedData?.id],
    enabled: !!(recordModalState['view-evaluation'] && recordSelectedData)
  })

  const salaryTotal = useMemo(
    () =>
      tableData.reduce((total, application) => {
        return total + (getPcrApplicationSalary(application, payScale) ?? 0)
      }, 0),
    [payScale, tableData]
  )

  const renderLanguages = (application: StaffApplication) => (
    <div className="flex w-16 flex-wrap gap-1">
      {application.languagesTaught.map((lang) => {
        if (lang.level === LanguageLevelEnum.NOT_PRACTICED) return null
        const Icon = languageMapping[lang.language].icon
        return <Icon key={lang.id} className="size-5 rounded-full" />
      })}
    </div>
  )

  const renderPaymentStatus = (application: StaffApplication) => {
    const scoreCard = application.pcrScoreCard

    if (scoreCard?.paidToPCR)
      return (
        <div className="flex flex-col items-center gap-1">
          <span>{paymentStatusMapping(scoreCard.paidToPCR)}</span>
          {scoreCard.dateOfPayment && <TableDate date={scoreCard.dateOfPayment} />}
        </div>
      )

    return paymentStatusMapping(scoreCard?.paidToPCR)
  }

  const renderSummaryButton = (application: StaffApplication) => (
    <div
      className="bg-primary-light rounded-full w-fit p-1 cursor-pointer"
      onClick={(e) => {
        e.stopPropagation()
        toggleRecordModal('view-summary', application)
      }}
      onMouseOver={() => toggleRecordModal('view-summary', application)}
    >
      <EyeOpenIcon width="22px" height="22px" color={tailwindColors.primary.DEFAULT} />
    </div>
  )

  const columns: Column<StaffApplication>[] = [
    {
      accessorKey: 'type',
      header: 'Poste',
      enableSorting: true,
      cell: (data) => staffApplicationTypeMapping[data.row.original.type].label
    },
    {
      accessorKey: 'lastName',
      header: 'NOM',
      enableSorting: true,
      cell: (data) => data.row.original.lastName.toUpperCase()
    },
    {
      accessorKey: 'firstName',
      header: 'Prénom',
      enableSorting: true,
      cell: (data) => StringFormatter.capitalize(data.row.original.firstName)
    },
    {
      accessorKey: 'birthDate',
      header: 'Âge',
      enableSorting: true,
      sortDescFirst: true,
      invertSorting: true,
      cell: (data) => DateFormatter.calculateAge(new Date(data.row.original.birthDate))
    },
    {
      accessorKey: 'languagesTaught',
      header: 'Langue',
      cell: (data) => renderLanguages(data.row.original)
    },
    {
      accessorKey: 'dateAssigned',
      header: 'Date d’envoi au PCR',
      enableSorting: true,
      cell: (data) =>
        data.row.original.dateAssigned ? <TableDate date={data.row.original.dateAssigned} /> : '-'
    },
    {
      accessorKey: 'priority',
      header: 'Priorité',
      enableSorting: true,
      centerCell: true,
      cell: (data) => (
        <Badge
          text={priorityEnumMapping[data.row.original.priority].label}
          variant={priorityEnumMapping[data.row.original.priority].variant}
        />
      )
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      enableSorting: true,
      centerCell: true,
      cell: (data) => <ApplicationStatusBadge application={data.row.original} />
    },
    {
      accessorKey: 'id',
      header: 'Évaluation du PCR',
      cell: (data) => (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            toggleRecordModal('view-evaluation', data.row.original)
          }}
          label="Voir"
          icon={PlusIcon}
          disabled={!data.row.original.currentScoreCard}
        />
      )
    },
    {
      accessorKey: 'currentScoreCard.isApplicantRecommendedForHire',
      header: 'Retour du PCR',
      enableSorting: true,
      cell: (data) => (
        <Badge
          text={
            pcrResult(
              data.row.original.currentScoreCard?.isApplicantRecommendedForHire,
              data.row.original.pcrId
            ).label
          }
          variant={
            pcrResult(data.row.original.currentScoreCard?.isApplicantRecommendedForHire).variant
          }
        />
      )
    },
    {
      accessorKey: 'salary',
      header: 'Rémunération',
      enableSorting: false,
      centerCell: true,
      cell: (data) => formatPcrSalary(getPcrApplicationSalary(data.row.original, payScale))
    },
    {
      accessorKey: 'pcrScoreCard.paidToPCR',
      header: 'Statut du paiement',
      enableSorting: true,
      centerCell: true,
      cell: (data) => renderPaymentStatus(data.row.original)
    },
    {
      accessorKey: 'id',
      header: 'Consulter',
      centerCell: true,
      cell: (data) => renderSummaryButton(data.row.original)
    }
  ]

  const similarApplicationColumns: ExpandedColumn<StaffApplication, StaffApplication>[] = [
    {
      accessorKey: 'type',
      display: (a) => staffApplicationTypeMapping[a.type].label
    },
    {
      accessorKey: 'lastName',
      display: (a) => a.lastName.toUpperCase()
    },
    {
      accessorKey: 'firstName',
      display: (a) => StringFormatter.capitalize(a.firstName)
    },
    {
      accessorKey: 'birthDate',
      display: (a) => DateFormatter.calculateAge(new Date(a.birthDate))
    },
    {
      accessorKey: 'languagesTaught',
      display: renderLanguages
    },
    {
      accessorKey: 'dateAssigned',
      display: (a) => (a.dateAssigned ? <TableDate date={a.dateAssigned} /> : '-')
    },
    {
      accessorKey: 'priority',
      display: (a) => (
        <Badge
          text={priorityEnumMapping[a.priority].label}
          variant={priorityEnumMapping[a.priority].variant}
        />
      )
    },
    {
      accessorKey: 'status',
      display: (a) => <ApplicationStatusBadge application={a} />
    },
    {
      accessorKey: 'id',
      display: (a) => (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            toggleRecordModal('view-evaluation', a)
          }}
          label="Voir"
          icon={PlusIcon}
          size="small"
          disabled={!a.currentScoreCard}
        />
      )
    },
    {
      accessorKey: 'currentScoreCard.isApplicantRecommendedForHire',
      display: (a) => (
        <Badge
          text={pcrResult(a.currentScoreCard?.isApplicantRecommendedForHire, a.pcrId).label}
          variant={pcrResult(a.currentScoreCard?.isApplicantRecommendedForHire).variant}
        />
      )
    },
    {
      accessorKey: 'salary',
      display: (a) => formatPcrSalary(getPcrApplicationSalary(a, payScale))
    },
    {
      accessorKey: 'pcrScoreCard.paidToPCR',
      display: renderPaymentStatus
    },
    {
      accessorKey: 'id',
      display: renderSummaryButton
    }
  ]

  return (
    <div>
      <NestedDataTable<StaffApplication, StaffApplicationFilters, StaffApplication>
        ref={tableRef}
        title="Nombre total de candidats et leurs statuts"
        columns={columns}
        onDataChange={setTableData}
        onLoadingChange={setIsTableLoading}
        footerCells={[
          {
            accessorKey: 'salary',
            value: (
              <Typography
                variant="label-semibold"
                className="text-nowrap"
                color={tailwindColors.grey.DEFAULT}
              >
                Total : {formatPcrSalary(salaryTotal)}
              </Typography>
            )
          }
        ]}
        onFiltersClick={() => {
          toggleModal('filters')
        }}
        deleteMessage={`Êtes-vous sûr de vouloir supprimer cette candidature? `}
        selectable
        expandedOptions={{
          key: 'similarApplications',
          columns: similarApplicationColumns
        }}
        endpoint={API.candidate.getAll.url}
        version="FULL"
        onEditAction={(row) => {
          navigate(Paths.UPDATE_STAFF_APPLICATION_PAGE.replace(':id', row.id.toString()))
        }}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter, [
          'similarApplications',
          'showWithdrawApplications',
          ...(selectedFilter?.pcrId === pcrId ? (['pcrId'] as const) : [])
        ])}
        specialRows={[
          { color: tailwindColors.grey.light.DEFAULT, condition: (row) => row.pcrId !== pcrId }
        ]}
        actions={[
          {
            type: 'global',
            label: 'Voir le tableau Kanban',
            fn: () => {
              navigate(Paths.PCR_KANBAN_BOARD.replace(':id', pcrId.toString()))
            }
          },
          {
            type: 'global',
            icon: ChartIcon,
            label: 'Statistiques',
            variant: 'ghost',
            fn: () => {
              navigate(Paths.PCR_STATISTICS.replace(':id', pcrId.toString()))
            }
          },
          {
            type: 'multiple',
            icon: EuroIcon,
            label: 'Marquer comme payé',
            variant: 'ghost',
            fn: (applications) => {
              const allApplicationsAreValid = applications.every(
                (application) =>
                  application.status === StaffApplicationStatusEnum.CLASSIFIED ||
                  application.status === StaffApplicationStatusEnum.INTERVIEW_DONE ||
                  application.status === StaffApplicationStatusEnum.ACCEPTED ||
                  application.status === StaffApplicationStatusEnum.REJECTED_AFTER_INTERVIEW
              )
              if (!allApplicationsAreValid)
                toast.error('Les candidatures sélectionnées ne sont pas valides')
              else toggleModal('mark-as-paid', applications)
            }
          },
          {
            type: 'multiple',
            icon: UserCheckIcon,
            label: 'Valider',
            variant: 'ghost',
            requiredConfirmation: true,
            refetch: true,
            confirmText: 'Accepter',
            cancelText: 'Fermer',
            confirmationMessage: 'Êtes-vous sûr de vouloir valider cette candidature ?',
            fn: async (applications) => {
              const validatedApplications = await validateStaffApplicationsMutation({
                ids: applications.map((a) => a.id)
              })

              if (applications.length === 1 && validatedApplications[0]?.staffId)
                navigate(
                  Paths.STAFF_UPDATE_PAGE.replace(
                    ':id',
                    validatedApplications[0].staffId.toString()
                  )
                )
            }
          },
          {
            type: 'multiple',
            icon: UserCanceledIcon,
            label: 'Rejeter',
            variant: 'ghost',
            fn: (applications) => {
              toggleModal('reject-application', applications)
            }
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Envoyer un rappel au PCR',
            variant: 'ghost',
            successMessage: 'Rappel envoyé avec succès',
            fn: async (applications) => {
              await sendReminderToPcr({ ids: applications.map((a) => a.id) })
            }
          },
          {
            type: 'global',
            icon: WithdrawIcon,
            label: selectedFilter?.showWithdrawApplications
              ? 'Cacher les candidatures'
              : 'Voir candidatures retirées',
            variant: 'ghost',
            fn: () => {
              if (!selectedFilter) return
              onSelectedFilterChange({
                ...selectedFilter,
                showWithdrawApplications: !selectedFilter.showWithdrawApplications
              })
            }
          }
        ]}
      />

      <Modal
        isOpen={modalState['mark-as-paid']}
        title="Marquer comme payé"
        icon={EuroIcon}
        hideActions
        onCancel={() => toggleModal('mark-as-paid')}
      >
        <StaffApplicationMarkAsPaidForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{
            classifiedAmount: payScale?.classifiedAmount ?? 0,
            interviewDoneAmount: payScale?.interviewDoneAmount ?? 0,
            pcrId
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('mark-as-paid'),
            onConfirm: async (values) => {
              if (!selectedData) return
              await markAsPaidMutation({
                ids: selectedData.map((application) => application.id),
                ...values
              })
              tableRef.current?.refetch()
              closeModel('mark-as-paid')
            }
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState['reject-application']}
        title="Etes-vous sûr de vouloir rejeter cette candidature?"
        size="medium"
        icon={XIcon}
        variant="destructive"
        hideActions
        onCancel={() => closeModel('reject-application')}
        customActions={
          <div className="flex gap-4 justify-center">
            <Button
              label="Annuler"
              variant="ghost"
              onClick={() => closeModel('reject-application')}
            />
            <Button
              label="Rejeter en envoyant une réponse"
              onClick={async () => {
                if (!selectedData) return
                setRejectApplicationWithEmailLoading(true)
                await rejectApplicationMutation({
                  ids: selectedData.map((a) => a.id),
                  sendEmail: true
                })
                setRejectApplicationWithEmailLoading(false)
                tableRef.current?.refetch()
                closeModel('reject-application')
              }}
              loading={rejectApplicationWithEmailLoading}
            />
            <Button
              label="Rejeter sans envoyer de réponse"
              onClick={async () => {
                if (!selectedData) return
                setRejectApplicationLoading(true)
                await rejectApplicationMutation({
                  ids: selectedData.map((a) => a.id),
                  sendEmail: false
                })
                setRejectApplicationLoading(false)
                tableRef.current?.refetch()
                closeModel('reject-application')
              }}
              loading={rejectApplicationLoading}
            />
          </div>
        }
      />

      <Modal
        isOpen={recordModalState['view-summary']}
        size="large"
        hideActions
        hideHeader
        onCancel={() => toggleRecordModal('view-summary')}
      >
        {recordSelectedData && (
          <div className={MODAL_BODY_CLASSNAME}>
            <StaffApplicationSummary staffApplication={recordSelectedData} />
            <div className="h-4" />
          </div>
        )}
      </Modal>

      {recordSelectedData && currentScoreCard && (
        <PcrScoreCardModal
          status={recordSelectedData.status}
          readOnly
          defaultTab={2}
          modalLoading={isCurrentScoreCardPending}
          isOpen={recordModalState['view-evaluation']}
          onCancel={() => closeRecordModel('view-evaluation')}
          staffApplication={recordSelectedData}
          defaultValues={mapScoreCardToInput(currentScoreCard)}
          handleReset={() => closeRecordModel('view-evaluation')}
        />
      )}

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <StaffApplicationsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default PcrApplicationsTable
